import React, { useEffect, useMemo, useState } from 'react';
import api from '../api-instance';
import NavBar from './NavBar';
import toast, { Toaster } from 'react-hot-toast';

const initialFormState = {
  name: '',
  type: '',
  displayName: '',
  description: '',
  active: true,
  legacy: false,
  config: null,
};

const BannerTypes = () => {
  const [bannerTypes, setBannerTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(initialFormState);
  const [editingId, setEditingId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageConfigs, setImageConfigs] = useState([]); // Array of image configurations

  const sortedTypes = useMemo(
    () => [...bannerTypes].sort((a, b) => a.name.localeCompare(b.name)),
    [bannerTypes]
  );

  const fetchBannerTypes = async () => {
    setLoading(true);
    try {
      const response = await api.get('/banner-types');
      setBannerTypes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch banner types', error);
      toast.error(error.response?.data?.error || 'Failed to load banner types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBannerTypes();
  }, []);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const resetForm = () => {
    setForm(initialFormState);
    setImageConfigs([]);
    setEditingId(null);
  };

  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleEdit = (bannerType) => {
    setEditingId(bannerType.id);
    setForm({
      name: bannerType.name || '',
      type: bannerType.type || '',
      displayName: bannerType.displayName || '',
      description: bannerType.description || '',
      active: !!bannerType.active,
      legacy: !!bannerType.legacy,
      config: bannerType.config || null,
    });
    // Load image configs from banner type config
    if (bannerType.config && bannerType.config.images) {
      setImageConfigs(bannerType.config.images.map(img => ({
        id: img.id || '',
        type: img.type || 'file',
        required: img.required || false,
        name: img.name || '',
        width: img.size?.width || '',
        height: img.size?.height || '',
        ext: Array.isArray(img.ext) ? img.ext.join(', ') : (img.ext || ''),
      })));
    } else {
      setImageConfigs([]);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const addImageConfig = () => {
    const maxId = imageConfigs.length > 0 
      ? Math.max(...imageConfigs.map(img => parseInt(img.id) || 0))
      : 0;
    setImageConfigs([...imageConfigs, {
      id: maxId + 1,
      type: 'file',
      required: false,
      name: '',
      width: '',
      height: '',
      ext: '.png, .jpg, .gif',
    }]);
  };

  const removeImageConfig = (index) => {
    setImageConfigs(imageConfigs.filter((_, i) => i !== index));
  };

  const updateImageConfig = (index, field, value) => {
    const updated = [...imageConfigs];
    if (field === 'required') {
      updated[index][field] = value === 'true' || value === true;
    } else {
      updated[index][field] = value;
    }
    setImageConfigs(updated);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      // Convert image configs array to config object
      let parsedConfig = null;
      if (imageConfigs.length > 0) {
        parsedConfig = {
          images: imageConfigs.map((img, index) => ({
            id: img.id || index + 1,
            type: img.type || 'file',
            required: img.required || false,
            name: img.name || `image_${index + 1}`,
            size: {
              width: img.width || '100px',
              height: img.height || '200px',
            },
            ext: img.ext ? img.ext.split(',').map(e => e.trim()).filter(e => e) : ['.png', '.jpg', '.gif'],
          })),
        };
      }

      const payload = {
        ...form,
        active: Boolean(form.active),
        legacy: Boolean(form.legacy),
        config: parsedConfig,
      };

      if (editingId) {
        await api.put(`/banner-types/${editingId}`, payload);
        toast.success('Banner type updated');
      } else {
        await api.post('/banner-types', payload);
        toast.success('Banner type created');
      }

      await fetchBannerTypes();
      closeModal();
    } catch (error) {
      console.error('Failed to save banner type', error);
      toast.error(error.response?.data?.error || 'Failed to save banner type');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <NavBar />

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6">
          <div className="px-6 py-5 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Banner Types</h2>
              <p className="mt-1 text-sm text-slate-600">
                Manage available banner type definitions.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={fetchBannerTypes}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 transition-colors duration-200"
              >
                Refresh
              </button>
              <button
                type="button"
                onClick={openCreateModal}
                className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200"
              >
                Add Banner Type
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          {loading ? (
            <div className="px-6 py-12 text-center">
              <p className="text-sm text-slate-500">Loading banner types...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Display Name
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Config
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {sortedTypes.length === 0 ? (
                    <tr>
                      <td
                        className="px-6 py-12 text-sm text-slate-500 text-center"
                        colSpan="7"
                      >
                        No banner types found.
                      </td>
                    </tr>
                  ) : (
                    sortedTypes.map((item) => (
                      <tr key={item.id} className="hover:bg-slate-50 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                          {item.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{item.type}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {item.displayName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex items-center gap-2">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                item.active
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-slate-100 text-slate-800'
                              }`}
                            >
                              {item.active ? 'Active' : 'Inactive'}
                            </span>
                            {item.legacy && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                Legacy
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {item.description || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {item.config ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                              {item.config?.images?.length || 0} image{item.config?.images?.length !== 1 ? 's' : ''}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-xs">No config</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            type="button"
                            onClick={() => handleEdit(item)}
                            className="text-indigo-600 hover:text-indigo-700 transition-colors duration-200"
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="relative w-full max-w-4xl bg-white rounded-xl border border-slate-200 shadow-xl my-8">
            <div className="flex items-start justify-between border-b border-slate-200 px-6 py-5">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  {editingId ? 'Edit Banner Type' : 'Add Banner Type'}
                </h3>
                <p className="mt-1 text-sm text-slate-600">
                  Create or update banner type metadata used by banners.
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="text-slate-400 hover:text-slate-600 transition-colors duration-200"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-6 max-h-[calc(100vh-200px)] overflow-y-auto">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors duration-200"
                    placeholder="SideBanner1"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Type (slug)</label>
                  <input
                    name="type"
                    value={form.type}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors duration-200"
                    placeholder="sidebanner1"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Display Name</label>
                  <input
                    name="displayName"
                    value={form.displayName}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors duration-200"
                    placeholder="Side Banner 1"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Description
                  </label>
                  <input
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors duration-200"
                    placeholder="Optional description"
                  />
                </div>
                <div className="flex items-center gap-6 sm:col-span-2">
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      name="active"
                      checked={form.active}
                      onChange={handleChange}
                      className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span>Active</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      name="legacy"
                      checked={form.legacy}
                      onChange={handleChange}
                      className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span>Legacy</span>
                  </label>
                </div>
                <div className="sm:col-span-2">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-slate-700">
                      Image Configuration
                    </label>
                    <button
                      type="button"
                      onClick={addImageConfig}
                      className="inline-flex items-center px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200"
                    >
                      + Add Image
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mb-4">
                    Define image specifications for this banner type. Click "+ Add Image" to add multiple images.
                  </p>
                  
                  {imageConfigs.length === 0 ? (
                    <div className="text-sm text-slate-400 italic py-8 text-center border-2 border-dashed border-slate-300 rounded-lg bg-slate-50">
                      No images configured. Click "+ Add Image" to add one.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {imageConfigs.map((img, index) => (
                        <div key={index} className="border border-slate-200 rounded-lg p-5 bg-slate-50">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="text-sm font-medium text-slate-700">Image {index + 1}</h4>
                            <button
                              type="button"
                              onClick={() => removeImageConfig(index)}
                              className="text-red-600 hover:text-red-700 text-sm font-medium transition-colors duration-200"
                            >
                              Remove
                            </button>
                          </div>
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-2">
                                ID
                              </label>
                              <input
                                type="number"
                                value={img.id}
                                onChange={(e) => updateImageConfig(index, 'id', parseInt(e.target.value) || '')}
                                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors duration-200"
                                placeholder="1"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-2">
                                Type
                              </label>
                              <select
                                value={img.type}
                                onChange={(e) => updateImageConfig(index, 'type', e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors duration-200"
                              >
                                <option value="file">File</option>
                                <option value="url">URL</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-2">
                                Name
                              </label>
                              <input
                                type="text"
                                value={img.name}
                                onChange={(e) => updateImageConfig(index, 'name', e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors duration-200"
                                placeholder="banner_img_1"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-2">
                                Required
                              </label>
                              <select
                                value={img.required ? 'true' : 'false'}
                                onChange={(e) => updateImageConfig(index, 'required', e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors duration-200"
                              >
                                <option value="false">No</option>
                                <option value="true">Yes</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-2">
                                Width
                              </label>
                              <input
                                type="text"
                                value={img.width}
                                onChange={(e) => updateImageConfig(index, 'width', e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors duration-200"
                                placeholder="100px"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-2">
                                Height
                              </label>
                              <input
                                type="text"
                                value={img.height}
                                onChange={(e) => updateImageConfig(index, 'height', e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors duration-200"
                                placeholder="200px"
                              />
                            </div>
                            <div className="sm:col-span-2">
                              <label className="block text-xs font-medium text-slate-700 mb-2">
                                Extensions (comma separated)
                              </label>
                              <input
                                type="text"
                                value={img.ext}
                                onChange={(e) => updateImageConfig(index, 'ext', e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors duration-200"
                                placeholder=".png, .jpg, .gif"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3 border-t border-slate-200 pt-6">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {saving ? 'Saving...' : editingId ? 'Update Type' : 'Add Type'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Toaster />
    </div>
  );
};

export default BannerTypes;

